import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ExternalLink, Download } from "lucide-react";
import { googleCalUrl, outlookCalUrl, icsBlobUrl } from "@/lib/calendar";

// "Add to calendar" button with a Google / Apple (.ics) / Outlook chooser.
// Pass `children` + `inline` to render a custom inline trigger (e.g. the event date).
export default function AddToCalendar({ event, className = "", testid = "add-to-calendar", children = null, inline = false }) {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const [ics, setIcs] = useState(null);
    const ref = useRef(null);
    const btnRef = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    useEffect(() => {
        if (open && !ics) setIcs(icsBlobUrl(event));
        if (open && btnRef.current) {
            // Fixed-position menu: escapes any overflow-hidden ancestor and is clamped
            // to the viewport so it never runs off the top/bottom/side on mobile.
            const rect = btnRef.current.getBoundingClientRect();
            const width = 208; // w-52
            const estH = 150;
            const margin = 8;
            const spaceBelow = window.innerHeight - rect.bottom;
            const up = spaceBelow < estH + margin && rect.top > spaceBelow;
            let left = inline ? rect.left : rect.left;
            if (left + width > window.innerWidth - margin) left = window.innerWidth - margin - width;
            if (left < margin) left = margin;
            const style = { position: "fixed", left, width };
            if (up) style.bottom = window.innerHeight - rect.top + margin;
            else style.top = rect.bottom + margin;
            setMenuStyle(style);
        }
        return () => { if (ics) { URL.revokeObjectURL(ics); } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const g = googleCalUrl(event);
    const o = outlookCalUrl(event);
    if (!g) return null;
    const fileName = `${(event.name || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
    const item = "flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-[#0f0f0f] hover:text-[#22c55e] transition-colors";

    return (
        <span className={inline ? "relative inline-flex" : "relative block w-full"} ref={ref}>
            <button
                ref={btnRef}
                type="button"
                data-testid={testid}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                className={className || "w-full h-11 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-semibold bg-[#0f0f0f] border border-[#2a2a2a] text-white hover:border-[#22c55e] hover:text-[#22c55e] transition-colors"}
            >
                {children || (<><CalendarPlus className="h-3.5 w-3.5" /> Add to calendar</>)}
            </button>
            {open && menuStyle && (
                <div data-testid={`${testid}-menu`} style={menuStyle} className="z-[9999] bg-[#141414] border border-[#2a2a2a] shadow-2xl max-h-[70vh] overflow-y-auto">
                    <div className="px-3 py-2 border-b border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Add to calendar</div>
                    <a href={g} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-google`} onClick={() => setOpen(false)} className={item}>Google Calendar <ExternalLink className="h-3 w-3" /></a>
                    {ics && <a href={ics} download={fileName} data-testid={`${testid}-apple`} onClick={() => setOpen(false)} className={`${item} border-t border-[#2a2a2a]`}>Apple Calendar <Download className="h-3 w-3" /></a>}
                    <a href={o} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-outlook`} onClick={() => setOpen(false)} className={`${item} border-t border-[#2a2a2a]`}>Outlook <ExternalLink className="h-3 w-3" /></a>
                </div>
            )}
        </span>
    );
}
